import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import StepWizard from 'react-step-wizard';
import {
  Layout,
  Loading,
  ReturnButton,
  TranslucentContainer,
  UserNavbar,
} from '../../../components';
import { getRetailProfileById } from '../../../core/retail-profiles/action';
import {
  setVendorRetailPayload,
  resetVendorRetailState,
  findVendorRetailBooking,
} from '../../../core/vendor-retail/action';
import VendorInformation from './components/VendorInformation';
import DateTimeSelection from './components/DateTimeSelection';
import Charges from './components/Charges';
import Payment from './components/Payment';
import SuccessPage from './components/Success';
import WaitApprovalPage from './components/WaitApproval';
import DeniedPage from './components/Denied';
import CancelledPage from './components/Cancelled';
import { APP_ROUTES } from '../../../constants/app-routes';
import InfoRetailCard from './components/InfoRetailCard';

export default function RetailBooking() {
  const { id: retailProfileId } = useParams();
  const {
    retailProfiles: { profile, loading: profileLoading },
    users: { user },
    vendorRetail: { currentBooking, recentBookings, loading: bookingLoading },
  } = useSelector((store) => store);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetVendorRetailState());
    if (retailProfileId) {
      dispatch(getRetailProfileById(retailProfileId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retailProfileId]);

  useEffect(() => {
    if (user?.id && retailProfileId) {
      dispatch(
        setVendorRetailPayload({
          vendorId: user.id,
          retailScheduleId: null, // Will be set in DateTimeSelection
          venueId: profile?.venue_id,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.id, retailProfileId, profile?.venue_id]);

  const render = () => {
    // Check if any bookings have been created (either current or recent)
    const hasBookings =
      (currentBooking && currentBooking.id) || (recentBookings && recentBookings.length > 0);

    if (hasBookings) {
      // Use the most recent booking for status checks
      const bookingToCheck =
        currentBooking && currentBooking.id ? currentBooking : recentBookings[0];

      // Check if booking is cancelled
      if (bookingToCheck.has_been_cancelled) {
        return <CancelledPage />;
      }

      // Check payment status
      if (bookingToCheck.payment_status === 'paid') {
        return <SuccessPage />;
      }

      // Check application status
      switch (bookingToCheck.application_status) {
        case 'approved':
          return <Payment />;
        case 'pending':
        case 'incomplete':
          // Show waiting page for both pending and incomplete statuses
          return <WaitApprovalPage />;
        case 'denied':
          return <DeniedPage />;
        default:
          return <WaitApprovalPage />;
      }
    }

    // No booking yet, show the wizard
    return (
      <StepWizard>
        <DateTimeSelection />
        <VendorInformation />
        <Charges />
      </StepWizard>
    );
  };

  return (
    <Layout>
      {(profileLoading || bookingLoading) && <Loading />}
      <UserNavbar />
      <Grid container marginTop={9} marginLeft={4}>
        <Grid item xs={12} lg={8}>
          <ReturnButton path={APP_ROUTES.DASHBOARD} />
        </Grid>
      </Grid>

      <Grid container justifyContent="center">
        <TranslucentContainer>
          <Grid container justifyContent="center" marginBottom={2}>
            <Grid item xs={12} lg={8}>
              <InfoRetailCard profile={profile} />
            </Grid>
          </Grid>
          <Grid container justifyContent="center" marginBottom={2}>
            <Grid item xs={12} lg={8}>
              {render()}
            </Grid>
          </Grid>
        </TranslucentContainer>
      </Grid>
    </Layout>
  );
}
